import React, {useState, useEffect} from 'react'
import BloodPressureAlerts from './BloodPressureAlerts'
import UsageAlerts from './UsageAlerts'
import PatientsSinceLastReading from '../charts/PatientsSinceLastReading'
import RPMReadingsTrend from '../charts/RPMReadingsTrend'
import PatientCountByCareMinsGrpChart from '../charts/PatientCountByCareMinsGrpChart'
import {useReduxAuth} from '../../../auth'
import {RoleGuard} from '../permissions/RoleGuard'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import StaffDoctorMinutesChart from '../charts/StaffDoctorMinutesChart'
import {NavLink} from 'react-router-dom'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'

// Stable noop function to prevent useEffect dependency changes
const noop = () => {}

// Reusable Subscribe Button Component
const SubscribeButton: React.FC<{size?: 'sm' | 'regular'}> = ({size = 'regular'}) => (
  <NavLink to='/subscription'>
    <button className={`btn btn-primary btn-with-icon ${size === 'sm' ? 'btn-sm' : ''}`}>
      <i className='fas fa-credit-card me-2'></i>
      Subscribe Now
    </button>
  </NavLink>
)

// Reusable Banner Component
const InfoBanner: React.FC<{
  title: string
  message: string
  type?: 'info' | 'warning'
  showButton?: boolean
  buttonText?: string
  helperText?: string
}> = ({title, message, type = 'info', showButton = false, buttonText, helperText}) => (
  <div className='col-12'>
    <div className='card border-0 shadow-sm text-center py-4'>
      <div className='card-body p-0'>
        <h4 className='card-title text-dark mb-3'>{title}</h4>
        <p className='text-muted mb-4'>{message}</p>

        {showButton && (
          <div className='d-grid gap-2 d-md-flex justify-content-md-center mb-3'>
            <SubscribeButton />
          </div>
        )}

        {helperText && (
          <div className='alert alert-info mb-0' role='alert'>
            <i className='fas fa-info-circle me-2'></i>
            {helperText}
          </div>
        )}
      </div>
    </div>
  </div>
)

const DashboardClosedSidebar: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const setMaSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.setSelectedDoctorId : noop
  
  // State for doctor dropdown (for MA)
  const [maDoctors, setMaDoctors] = useState<Array<{id: number; full_name: string}>>([])
  
  // Check if user has active subscription or trial
  const hasActiveSubscription = currentUser?.subscription_status === 'active'
  // if user cancelled the subscription but can still access the dashboard
  // e.g. they have access until the end of the billing cycle
  const hasCancelledSubscription = currentUser?.subscription_status === 'cancelled'
  
  const hasActiveTrial =
    currentUser?.subscription_status === 'trial' || currentUser?.subscription_status === 'trialing'
  const hasAccess = hasActiveSubscription || hasActiveTrial || hasCancelledSubscription

  // Fetch all doctors for MA (MA can access all doctors via dropdown)
  useEffect(() => {
    if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT) {
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setMaDoctors(fetchedDoctors)
          if (fetchedDoctors.length > 0 && !maSelectedDoctorId) {
            setMaSelectedDoctorId(fetchedDoctors[0].id)
          }
        })
        .catch((err) => {
          console.error('Error fetching doctors for MA:', err)
          setMaDoctors([])
        })
    }
  }, [currentUser?.role_id, maSelectedDoctorId, setMaSelectedDoctorId])

  // Trial notification component - polite encouragement
  const TrialNotification = () => {
    const isDoctor = currentUser?.role_id === USER_ROLES.DOCTOR
    const isStaff = currentUser?.role_id === USER_ROLES.NURSE

    if (!hasActiveTrial) return null

    // Only show for clinical roles (doctors and nurses)
    if (!isDoctor && !isStaff) return null

    return (
      <div className='col-12 mb-3'>
        <div className='alert alert-info border-0 shadow-sm' role='alert'>
          <div className='d-flex align-items-center'>
            <div className='me-3'>
              <i className='fas fa-info-circle text-info' style={{fontSize: '1.5rem'}}></i>
            </div>
            <div className='flex-grow-1'>
              {isDoctor && (
                <>
                  <h6 className='alert-heading mb-2'>
                    <i className='fas fa-star text-warning me-2'></i>
                    You're Currently on a Trial Plan
                  </h6>
                  <p className='mb-2'>
                    We hope you're enjoying full access to all features! To continue uninterrupted
                    service, we'd appreciate if you could subscribe at your earliest convenience.
                  </p>
                  <div className='d-flex flex-wrap gap-2 align-items-center'>
                    <SubscribeButton />
                    <small className='text-muted'>
                      Questions? Contact us at{' '}
                      <a href='mailto:support@candihealth.com' className='text-decoration-none'>
                        support@candihealth.com
                      </a>
                    </small>
                  </div>
                </>
              )}

              {isStaff && (
                <>
                  <h6 className='alert-heading mb-2'>
                    <i className='fas fa-star text-warning me-2'></i>
                    Trial Access Active
                  </h6>
                  <p className='mb-2'>
                    You currently have full access through a trial plan. When convenient, please
                    consider requesting your administrator to subscribe for continued access to all
                    features.
                  </p>
                  <small className='text-muted'>
                    Need assistance? Our team is here to help at{' '}
                    <a href='mailto:support@candihealth.com' className='text-decoration-none'>
                      support@candihealth.com
                    </a>
                  </small>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Subscription prompt component for first sections
  const SubscriptionPrompt = () => {
    const isDoctor = currentUser?.role_id === USER_ROLES.DOCTOR
    const isStaff = currentUser?.role_id === USER_ROLES.NURSE

    if (isDoctor) {
      return (
        <InfoBanner
          title='Subscription Required'
          message='To access patient health alerts, usage monitoring, and real-time data, please subscribe to one of our plans.'
          showButton={true}
        />
      )
    }

    if (isStaff) {
      return (
        <InfoBanner
          title='Access Restricted'
          message='Real-time monitoring requires an active subscription. Please contact your doctor or administrator.'
        />
      )
    }

    return (
      <InfoBanner
        title='Access Restricted'
        message='Please contact your administrator for access to real-time monitoring features.'
      />
    )
  }

  // Normal dashboard content
  return (
    <>
      {/* CSS to fix anchor tag btn-primary text color */}
      <style>
        {`
          a.btn-primary,
          a.btn-primary:hover,
          a.btn-primary:focus,
          a.btn-primary:active,
          a.btn-primary:visited {
            color: white !important;
            text-decoration: none !important;
          }
        `}
      </style>
      <div className={`container-fluid px-0 px-lg-3 mb-10 dashboardClosedSidebar`}>
        {' '}
        <div className='row g-4'>
          {/* Doctor Dropdown for MA */}
          {currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && (
            <div className='col-12 mb-3'>
              <div className='d-flex justify-content-end'>
                <div className='w-25'>
                  <label htmlFor='doctorSelect' className='form-label'>
                    Select Doctor
                  </label>
                  <select
                    id='doctorSelect'
                    className='form-select'
                    value={maSelectedDoctorId ?? ''}
                    onChange={(e) => {
                      const val = e.target.value
                      setMaSelectedDoctorId(val ? Number(val) : undefined)
                    }}
                    disabled={maDoctors.length === 0}
                  >
                    {maDoctors.length === 0 ? (
                      <option value=''>-- No doctors available --</option>
                    ) : (
                      <>
                        <option value=''>-- Select Doctor --</option>
                        {maDoctors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.full_name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              </div>
            </div>
          )}
          {/* Trial notification for trial users */}
          <TrialNotification />

          {/* First three sections - show subscription prompt if no access */}
          {hasAccess ? (
            <>
              <div className='col-12'>
                {/* TODO::Rename to Health Alerts, its not BP only */}
                <BloodPressureAlerts />
              </div>
              <div className='col-12 col-lg-8'>
                <UsageAlerts />
              </div>
              <div className='col-12 col-lg-4'>
                <PatientsSinceLastReading />
              </div>
            </>
          ) : (
            <SubscriptionPrompt />
          )}

          {/* <div className='col-12 col-lg-6'>
            <PatientStagesComparison />
          </div> */}

          {/* Billing charts - always show for doctors, nurses, and medical assistants */}
          <RoleGuard allowedRoles={[USER_ROLES.NURSE, USER_ROLES.DOCTOR, USER_ROLES.MEDICAL_ASSISTANT]}>
            <>
              <div className='col-12 col-lg-6'>
                <StaffDoctorMinutesChart />
              </div>
              <div className='col-12 col-lg-6'>
                <PatientCountByCareMinsGrpChart />
              </div>
            </>
          </RoleGuard>
          <div className='col-12'>
            <RPMReadingsTrend />
          </div>
        </div>
      </div>
    </>
  )
}

export default DashboardClosedSidebar
